import { useState, useEffect } from 'react'
import ReactInputVerificationCode from 'react-input-verification-code'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ClipLoader from 'react-spinners/ClipLoader'
import * as yup from 'yup'
import { forgotPassword } from '@/services/auth/forgot-password.service'
import { ForgotPasswordResponse } from '@/services/auth/types'
import { ApiResponse } from '@/services/types'
import {
  Container,
  LoginWrapper,
  Logo,
  LoginCard,
  InputBlock,
  EmailSentScreen,
} from '../../components'
import {
  Divider,
  Title,
  FormGroup,
  Button,
  TextWrapper,
  Error,
  CodeVerificationWrapper,
  Paragraph,
  Text,
  Line,
  TextLink,
} from './styled'

type ForgotPasswordData = {
  userName: string
}

const schema = yup
  .object({
    userName: yup.string().required('Email is required'),
  })
  .required()

const ForgotPassword = () => {
  const [error, setError] = useState(false)
  const [code, setCode] = useState('')
  const navigate = useNavigate()
  let [searchParams] = useSearchParams()
  const fromEmail = searchParams.get('success') === 'true' ? true : false
  const username = searchParams.get('username')
  const prefilledEmail = searchParams.get('email')
  const redirectUrl = searchParams.get('redirectUrl')
  const [success, setSuccess] = useState(fromEmail)
  const [loginUrlSent, setLoginUrlSent] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordData>({
    resolver: yupResolver(schema),
  })

  // Set prefilled email if available
  useEffect(() => {
    if (prefilledEmail) {
      setValue('userName', prefilledEmail)
    }
  }, [prefilledEmail, setValue])

  const workEmailRegister = { ...register('userName') }
  const emailWatcher = watch('userName')

  const onSubmit = handleSubmit(async (data: ForgotPasswordData) => {
    setError(false)
    try {
      const response: ApiResponse<ForgotPasswordResponse> =
        await forgotPassword({
          username: data.userName,
        })
      if (response.ok && response.data?.result === 'LOGIN_URL_SENT') {
        setLoginUrlSent(true)
      } else if (response.ok && response.data?.result === 'CODE_SENT') {
        setSuccess(true)
      } else {
        setError(true)
      }
    } catch (error) {
      setError(true)
    }
  })

  const onResendCode = async () => {
    setResendSuccess(false)
    try {
      const response: ApiResponse<ForgotPasswordResponse> =
        await forgotPassword({
          username: username || emailWatcher,
        })
      if (response.ok && response.data?.result === 'CODE_SENT') {
        setResendSuccess(true)
        setTimeout(() => setResendSuccess(false), 3000) // Hide message after 3 seconds
      }
    } catch (error) {
      // Handle error silently for resend
    }
  }

  // Show login link sent message
  if (loginUrlSent) {
    return <EmailSentScreen onGoBack={() => setLoginUrlSent(false)} />
  }

  return (
    <Container height="100vh">
      <LoginWrapper>
        <Logo />
        <LoginCard>
          {!success && (
            <>
              <Divider />
              <Title>Get help logging in</Title>
              <Text>
                Please enter your email below and we'll help you access your
                account.
              </Text>
              <FormGroup onSubmit={onSubmit}>
                <InputBlock
                  label="Work Email"
                  isMail
                  type="email"
                  params={workEmailRegister}
                  errorMessage={errors.userName?.message}
                  marginBottom="32px"
                  onChange={register('userName').onChange}
                />
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <ClipLoader
                      color="#ffffff"
                      loading={isSubmitting}
                      size={15}
                    />
                  ) : (
                    `Continue`
                  )}
                </Button>
                {error && <Error>An error occurred. Please try again.</Error>}
              </FormGroup>
              <Line />
              <TextLink
                to={`/login?email=${encodeURIComponent(
                  emailWatcher || prefilledEmail || '',
                )}${
                  redirectUrl
                    ? `&redirectUrl=${encodeURIComponent(redirectUrl)}`
                    : ''
                }`}
                rel="noopener noreferrer"
              >
                Back to Login
              </TextLink>
            </>
          )}
          {success && (
            <>
              <Divider />
              <Title>Enter verification code</Title>
              <Paragraph>
                Please open your email inbox and enter the 6-digit verification
                code we&apos;ve just sent you here.
              </Paragraph>
              <CodeVerificationWrapper>
                <div className="custom-styles">
                  <ReactInputVerificationCode
                    length={6}
                    placeholder=""
                    onChange={setCode}
                  />
                </div>
              </CodeVerificationWrapper>
              <Button
                type="submit"
                disabled={code.length < 6}
                onClick={() =>
                  navigate(
                    `/change-password/?username=${
                      getValues('userName') || username
                    }&code=${code}`,
                  )
                }
              >
                {code.length < 6
                  ? `${6 - code.length} digits left`
                  : `Send Code`}
              </Button>
              <Button onClick={onResendCode} isSecondary>
                Resend Code
              </Button>
              {resendSuccess && (
                <Paragraph
                  style={{
                    color: '#28a745',
                    marginTop: '12px',
                    textAlign: 'center',
                  }}
                >
                  Code resent successfully!
                </Paragraph>
              )}
              <Line />
              <TextLink
                to={`/login?email=${encodeURIComponent(
                  emailWatcher || username || prefilledEmail || '',
                )}${
                  redirectUrl
                    ? `&redirectUrl=${encodeURIComponent(redirectUrl)}`
                    : ''
                }`}
                rel="noopener noreferrer"
              >
                Back to Login
              </TextLink>
            </>
          )}
        </LoginCard>
      </LoginWrapper>
    </Container>
  )
}

export default ForgotPassword
